"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { motion, type Variants } from "motion/react";
import { Play, ArrowUpRight, ShieldCheck, MessageSquare } from "lucide-react";
import {
    buildEmbedUrl,
    derivePoster,
    PLATFORM_META,
    EMBED_SANDBOX,
    EMBED_ALLOW,
} from "@/lib/socialEmbed";
import { socialEmbeds, socialLinks } from "@/data/social";
import type { SocialEmbed, EmbedSize } from "@/types/social";

/* ── Motion ─────────────────────────────────────────────────────────────── */

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const gridVariants: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06 } },
};

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
};

/* ── Brand glyphs ───────────────────────────────────────────────────────── */

const XGlyph = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
        <path d="M18.9 2H22l-7.4 8.5L23 22h-6.8l-5.3-7-6.1 7H1.7l8-9.1L1 2h7l4.8 6.3L18.9 2Zm-1.2 18h1.9L7.4 3.9H5.4L17.7 20Z" />
    </svg>
);

const TikTokGlyph = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
        <path d="M16.6 5.8a5 5 0 0 1-1.1-3.3h-3v13.1a2.9 2.9 0 1 1-2-2.8V9.7a5.9 5.9 0 1 0 5 5.8V9.1a8 8 0 0 0 4.6 1.5V7.5a4.8 4.8 0 0 1-3.5-1.7Z" />
    </svg>
);

const FacebookGlyph = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
        <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12Z" />
    </svg>
);

const LinkedInGlyph = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
        <path d="M20.4 2H3.6A1.6 1.6 0 0 0 2 3.6v16.8A1.6 1.6 0 0 0 3.6 22h16.8a1.6 1.6 0 0 0 1.6-1.6V3.6A1.6 1.6 0 0 0 20.4 2ZM8 19H5V9.5h3V19ZM6.5 8.2a1.7 1.7 0 1 1 0-3.5 1.7 1.7 0 0 1 0 3.5ZM19 19h-3v-5c0-1.2-.4-2-1.5-2A1.6 1.6 0 0 0 13 13.6V19h-3V9.5h2.9v1.3a3.2 3.2 0 0 1 2.9-1.6c2.1 0 3.2 1.4 3.2 4V19Z" />
    </svg>
);

const GLYPH: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
    facebook: FacebookGlyph,
    x: XGlyph,
    tiktok: TikTokGlyph,
    linkedin: LinkedInGlyph,
};

/* ── Size → layout mapping ──────────────────────────────────────────────── */

const SIZE_SPAN: Record<EmbedSize, string> = {
    default: "lg:col-span-4",
    wide: "lg:col-span-8",
    tall: "lg:col-span-4 lg:row-span-2",
    square: "lg:col-span-4",
};

const SIZE_MEDIA: Record<EmbedSize, string> = {
    default: "aspect-video",
    wide: "aspect-video",
    tall: "aspect-[9/13] sm:aspect-[9/12]",
    square: "aspect-square",
};

/* ── Card ───────────────────────────────────────────────────────────────── */

function EmbedCard({ embed, eager }: { embed: SocialEmbed; eager: boolean }) {
    const [active, setActive] = useState(false);

    const meta = PLATFORM_META[embed.platform];
    const size = embed.size ?? "default";
    const poster = derivePoster(embed);
    const Glyph = GLYPH[meta.group] ?? MessageSquare;
    const isVideo = meta.kind === "video";

    return (
        <motion.article
            variants={cardVariants}
            className={`
        group relative flex min-w-0 flex-col overflow-hidden
        border border-[#17160F]/10 bg-[#FDFCFA]
        shadow-[0_1px_2px_rgba(23,22,15,0.04),0_18px_40px_-28px_rgba(23,22,15,0.22)]
        transition-shadow duration-500
        hover:shadow-[0_1px_2px_rgba(23,22,15,0.05),0_26px_56px_-28px_rgba(23,22,15,0.3)]
        w-[82vw] max-w-[340px] shrink-0 snap-center
        sm:w-auto sm:max-w-none
        ${SIZE_SPAN[size]}
      `}
        >
            {/* ── Media / facade ── */}
            <div className={`relative w-full overflow-hidden bg-[#EAE4D8] ${SIZE_MEDIA[size]}`}>
                {active ? (
                    <iframe
                        src={buildEmbedUrl(embed.platform, embed.ref, isVideo)}
                        title={embed.title}
                        loading="lazy"
                        allow={EMBED_ALLOW}
                        allowFullScreen
                        sandbox={EMBED_SANDBOX}
                        referrerPolicy="strict-origin-when-cross-origin"
                        className="absolute inset-0 h-full w-full border-0 bg-white"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => setActive(true)}
                        aria-label={`Load ${meta.label} ${meta.kind}: ${embed.title}`}
                        className="
              absolute inset-0 h-full w-full cursor-pointer
              outline-none focus-visible:ring-2 focus-visible:ring-inset
              focus-visible:ring-[#9C7A46]
            "
                    >
                        {poster ? (
                            <Image
                                src={poster}
                                alt=""
                                fill
                                loading={eager ? "eager" : "lazy"}
                                quality={78}
                                sizes="(max-width: 639px) 82vw, (max-width: 1023px) 50vw, 33vw"
                                className="
                  transform-gpu object-cover
                  transition-transform duration-[900ms] ease-out
                  motion-safe:group-hover:scale-[1.05]
                "
                            />
                        ) : (
                            /* No poster → engraved placeholder */
                            <span
                                aria-hidden="true"
                                className="absolute inset-0 grid place-items-center bg-[#EAE4D8]"
                                style={{
                                    backgroundImage:
                                        "linear-gradient(rgba(23,22,15,0.045) 1px,transparent 1px),linear-gradient(90deg,rgba(23,22,15,0.045) 1px,transparent 1px)",
                                    backgroundSize: "22px 22px",
                                }}
                            >
                                <Glyph className="h-10 w-10 text-[#17160F]/20" />
                            </span>
                        )}

                        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#17160F]/55 via-[#17160F]/5 to-transparent" />

                        {/* Platform chip */}
                        <span
                            className="
                absolute left-3 top-3 inline-flex items-center gap-1.5
                bg-[#F7F5F0]/90 px-2.5 py-1 backdrop-blur-sm
                text-[9px] font-semibold uppercase tracking-[0.2em] text-[#17160F]
                sm:text-[10px] sm:tracking-[0.24em]
              "
                        >
                            <Glyph className="h-3 w-3" style={{ color: meta.accent }} />
                            {meta.label}
                        </span>

                        {/* Play / load affordance */}
                        <span
                            className="
                absolute left-1/2 top-1/2 grid -translate-x-1/2 -translate-y-1/2
                place-items-center rounded-full
                h-14 w-14 sm:h-16 sm:w-16
                border border-[#F7F5F0]/70 bg-[#17160F]/45 text-[#F7F5F0]
                backdrop-blur-[2px]
                transition-all duration-500
                motion-safe:group-hover:scale-[1.08]
                motion-safe:group-hover:bg-[#17160F]/70
              "
                        >
                            {isVideo ? (
                                <Play
                                    size={20}
                                    strokeWidth={1.6}
                                    className="translate-x-[2px] fill-current"
                                />
                            ) : (
                                <ArrowUpRight size={20} strokeWidth={1.6} />
                            )}
                        </span>

                        <span className="absolute inset-x-0 bottom-0 p-3 text-left sm:p-4">
                            <span className="block text-[10px] uppercase tracking-[0.22em] text-[#F7F5F0]/70">
                                {isVideo ? "Tap to play" : "Tap to load post"}
                            </span>
                        </span>
                    </button>
                )}
            </div>

            {/* ── Caption plate ── */}
            <div className="flex flex-1 flex-col gap-2 border-t border-[#17160F]/[0.07] p-4 sm:p-5">
                <h3 className="text-[14px] font-medium leading-snug tracking-tight text-[#17160F] sm:text-[15px]">
                    {embed.title}
                </h3>

                {embed.caption && (
                    <p className="text-[12.5px] font-light leading-[1.65] text-[#5A5548] sm:text-[13px]">
                        {embed.caption}
                    </p>
                )}

                <a
                    href={embed.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
            mt-auto inline-flex w-fit items-center gap-1.5 pt-2
            text-[10px] font-semibold uppercase tracking-[0.24em] text-[#9C7A46]
            transition-colors duration-300 hover:text-[#17160F]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9C7A46]
          "
                >
                    View on {meta.label}
                    <ArrowUpRight
                        size={12}
                        strokeWidth={2.2}
                        className="transition-transform duration-300 group-hover:translate-x-0.5"
                    />
                </a>
            </div>
        </motion.article>
    );
}

/* ── Section ────────────────────────────────────────────────────────────── */

export function SocialEmbedSection({
    embeds = socialEmbeds,
}: {
    embeds?: SocialEmbed[];
}) {
    const [filter, setFilter] = useState<string>("all");

    const groups = useMemo(() => {
        const set = new Set(embeds.map((e) => PLATFORM_META[e.platform].group));
        return ["all", ...Array.from(set)];
    }, [embeds]);

    const visible = useMemo(
        () =>
            filter === "all"
                ? embeds
                : embeds.filter((e) => PLATFORM_META[e.platform].group === filter),
        [embeds, filter]
    );

    if (!embeds.length) return null;

    return (
        <section
            aria-labelledby="social-heading"
            className="relative w-full overflow-x-clip bg-[#F7F5F0] py-16 sm:py-20 lg:py-28"
        >
            <div className="mx-auto max-w-[1360px] px-[max(1.25rem,env(safe-area-inset-left))] sm:px-10 lg:px-16 xl:px-24">

                {/* ── Header ── */}
                <motion.div
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16"
                >
                    <div className="relative min-w-0 pl-5 sm:pl-8">
                        <span
                            aria-hidden="true"
                            className="absolute bottom-1 left-0 top-1 w-px bg-gradient-to-b from-[#9C7A46] via-[#9C7A46]/30 to-transparent"
                        />

                        <p className="text-[10.5px] font-semibold uppercase tracking-[0.28em] text-[#9C7A46] sm:text-[12px] sm:tracking-[0.4em]">
                            Broadcast
                            <span className="mx-2 text-[#17160F]/25">—</span>
                            Social
                        </p>

                        <h2
                            id="social-heading"
                            className="
                mt-4 flex flex-col font-display leading-[0.94] tracking-tight text-[#17160F]
                text-[clamp(2rem,8.5vw,3.2rem)]
                sm:mt-5 sm:text-[clamp(2.6rem,6vw,4rem)]
                lg:mt-6 lg:leading-[0.92] lg:text-[clamp(3rem,4.4vw,4.6rem)]
                [overflow-wrap:anywhere]
              "
                        >
                            <span className="font-light">Follow the</span>
                            <span className="font-black uppercase tracking-tighter">Making</span>
                        </h2>

                        <p className="mt-5 max-w-[46ch] text-[14.5px] font-light leading-[1.7] text-[#5A5548] sm:text-[15px] lg:text-[16px]">
                            Films, process notes and dispatches — published across our channels
                            and gathered here.
                        </p>
                    </div>

                    {/* ── Follow rail ── */}
                    <div className="flex shrink-0 flex-wrap items-center gap-2.5 lg:mb-1">
                        {(Object.keys(socialLinks) as (keyof typeof socialLinks)[]).map((key) => {
                            const G = GLYPH[key] ?? MessageSquare;
                            return (
                                <a
                                    key={key}
                                    href={socialLinks[key]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Follow us on ${key}`}
                                    className="
                    group grid h-12 w-12 place-items-center
                    border border-[#17160F]/15 bg-transparent text-[#17160F]
                    transition-colors duration-300
                    hover:border-[#17160F] hover:bg-[#17160F] hover:text-[#F7F5F0]
                    focus-visible:outline-none focus-visible:ring-2
                    focus-visible:ring-[#9C7A46] focus-visible:ring-offset-2
                    focus-visible:ring-offset-[#F7F5F0]
                  "
                                >
                                    <G className="h-[17px] w-[17px]" />
                                </a>
                            );
                        })}
                    </div>
                </motion.div>

                {/* ── Filter tabs ── */}
                {groups.length > 2 && (
                    <div
                        role="tablist"
                        aria-label="Filter by platform"
                        className="
              mt-10 flex gap-2 overflow-x-auto pb-1 sm:mt-12
              [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            "
                    >
                        {groups.map((g) => {
                            const on = filter === g;
                            return (
                                <button
                                    key={g}
                                    role="tab"
                                    aria-selected={on}
                                    onClick={() => setFilter(g)}
                                    className={`
                    shrink-0 border px-4 py-2.5 min-h-[44px]
                    text-[10px] font-semibold uppercase tracking-[0.22em]
                    transition-colors duration-300
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9C7A46]
                    ${on
                                            ? "border-[#17160F] bg-[#17160F] text-[#F7F5F0]"
                                            : "border-[#17160F]/15 text-[#5A5548] hover:border-[#17160F]/40 hover:text-[#17160F]"
                                        }
                  `}
                                >
                                    {g === "all" ? "All" : g}
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="mt-8 h-px w-full bg-[#17160F]/10 sm:mt-10" />
            </div>

            {/* ── Grid ──
          < 640px  → edge-to-edge snap carousel
          ≥ 640px  → 2 columns
          ≥ 1024px → 12-track bento
      */}
            <motion.div
                key={filter}
                variants={gridVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="
          mt-8 flex gap-4
          snap-x snap-mandatory overflow-x-auto overscroll-x-contain pb-2
          [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          px-[max(1.25rem,env(safe-area-inset-left))]
          sm:mx-auto sm:mt-10 sm:grid sm:max-w-[1360px] sm:grid-cols-2
          sm:overflow-visible sm:px-10 sm:pb-0
          lg:mt-14 lg:auto-rows-min lg:grid-cols-12 lg:px-16
          xl:px-24
        "
            >
                {visible.map((embed, i) => (
                    <EmbedCard key={embed.id} embed={embed} eager={i < 2} />
                ))}
                <span aria-hidden="true" className="w-2 shrink-0 sm:hidden" />
            </motion.div>

            {/* ── Privacy note ── */}
            <div
                className="
          mx-auto mt-8 flex max-w-[1360px] items-start gap-2.5
          px-[max(1.25rem,env(safe-area-inset-left))]
          sm:mt-10 sm:px-10 lg:px-16 xl:px-24
        "
            >
                <ShieldCheck
                    size={13}
                    strokeWidth={1.8}
                    className="mt-px shrink-0 text-[#9C7A46]"
                />
                <p className="text-[11px] font-light leading-relaxed text-[#5A5548]/80">
                    Nothing loads until you choose. Selecting a post connects you to that
                    platform, which may set cookies under its own privacy policy.
                </p>
            </div>

            <p className="mt-5 px-5 text-center text-[10px] uppercase tracking-[0.28em] text-[#17160F]/35 sm:hidden">
                Swipe to explore
            </p>
        </section>
    );
}

export default SocialEmbedSection;